// v3
// Path: frontend/src/components/DataCanvasV3/ConnectionManager.jsx
// Inputs: onAddConnection callback, onClose callback, token
// Outputs: React Component rendering 7 cascading connection dropdowns ending in Target Table selection.
// Summary: Cascading infrastructure selection wizard (Org -> Device -> Instance -> Interface -> Login -> Database -> Table). Lazy-loads live databases from /api/v3/databases for selected SQL instances. Resolves concrete root table before entering Data Canvas modal.

import React, { useState, useEffect, useMemo } from 'react';

const renderOrgOptions = (orgs, parentId = null, depth = 0) => {
    let options = [];
    const children = orgs.filter(o => o.ParentOrganizationID === parentId);
    children.forEach(child => {
        const prefix = '\u00A0\u00A0'.repeat(depth);
        options.push(
            <option key={child.OrganizationID} value={child.OrganizationID}>
                {prefix}{child.OrganizationName}
            </option>
        );
        options = options.concat(renderOrgOptions(orgs, child.OrganizationID, depth + 1));
    });
    return options;
};

const ConnectionManager = ({ onAddConnection, onClose, token }) => {
    const [organizations, setOrganizations] = useState([]);
    const [devices, setDevices] = useState([]);
    const [instances, setInstances] = useState([]);
    const [interfaces, setInterfaces] = useState([]);
    const [credentials, setCredentials] = useState([]);

    const [liveDbs, setLiveDbs] = useState([]);
    const [loadingDbs, setLoadingDbs] = useState(false);
    const [dbError, setDbError] = useState(null);

    const [tables, setTables] = useState([]);
    const [loadingTables, setLoadingTables] = useState(false);

    const [selectedOrgId, setSelectedOrgId] = useState('');
    const [selectedDeviceId, setSelectedDeviceId] = useState('');
    const [selectedInstanceId, setSelectedInstanceId] = useState('');
    const [selectedInterface, setSelectedInterface] = useState('');
    const [selectedLogin, setSelectedLogin] = useState('');
    const [selectedDatabase, setSelectedDatabase] = useState('');
    const [selectedTable, setSelectedTable] = useState('');

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchConnections = async () => {
            try {
                const res = await fetch('/api/v3/connections', {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!res.ok) throw new Error('Failed to fetch connections');
                const data = await res.json();
                
                setOrganizations(data.organizations || []);
                setDevices(data.devices || []);
                setInstances(data.instances || []);
                setInterfaces(data.interfaces || []);
                setCredentials(data.credentials || []);
                
                if (data.organizations && data.organizations.length > 0) {
                    const rootOrg = data.organizations.find(o => o.ParentOrganizationID === null) || data.organizations[0];
                    setSelectedOrgId(rootOrg.OrganizationID.toString());
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchConnections();
    }, [token]);

    const availableDevices = useMemo(() => 
        devices.filter(d => selectedOrgId ? d.OrganizationID?.toString() === selectedOrgId.toString() : true), 
    [devices, selectedOrgId]);

    const availableInstances = useMemo(() => 
        instances.filter(i => selectedDeviceId ? i.DeviceID?.toString() === selectedDeviceId.toString() : false),
    [instances, selectedDeviceId]);

    const availableInterfaces = useMemo(() => 
        interfaces.filter(ifc => selectedDeviceId ? ifc.DeviceID?.toString() === selectedDeviceId.toString() : false),
    [interfaces, selectedDeviceId]);

    const availableCredentials = useMemo(() => 
        credentials.filter(c => selectedInstanceId ? c.InstanceID?.toString() === selectedInstanceId.toString() : false),
    [credentials, selectedInstanceId]);

    useEffect(() => {
        if (availableDevices.length > 0 && (!selectedDeviceId || !availableDevices.find(d => d.DeviceID.toString() === selectedDeviceId))) {
            setSelectedDeviceId(availableDevices[0].DeviceID.toString());
        } else if (availableDevices.length === 0) {
            setSelectedDeviceId('');
        }
    }, [availableDevices, selectedDeviceId]);

    useEffect(() => {
        if (availableInstances.length > 0 && (!selectedInstanceId || !availableInstances.find(i => i.InstanceID.toString() === selectedInstanceId))) {
            setSelectedInstanceId(availableInstances[0].InstanceID.toString());
        } else if (availableInstances.length === 0) {
            setSelectedInstanceId('');
        }
    }, [availableInstances, selectedInstanceId]);

    useEffect(() => {
        if (availableInterfaces.length > 0 && (!selectedInterface || !availableInterfaces.find(i => i.IPAddress === selectedInterface))) {
            setSelectedInterface(availableInterfaces[0].IPAddress);
        } else if (availableInterfaces.length === 0) {
            setSelectedInterface('');
        }
    }, [availableInterfaces, selectedInterface]);

    useEffect(() => {
        if (availableCredentials.length > 0 && (!selectedLogin || !availableCredentials.find(c => c.Username === selectedLogin))) {
            setSelectedLogin(availableCredentials[0].Username);
        } else if (availableCredentials.length === 0) {
            setSelectedLogin('sa');
        }
    }, [availableCredentials, selectedLogin]);

    // Dynamic Fetch of Live Databases for targeted SQL Instance
    useEffect(() => {
        const fetchLiveDatabases = async () => {
            if (!selectedInstanceId || !token) {
                setLiveDbs([]);
                setSelectedDatabase('');
                return;
            }

            const selectedInst = instances.find(i => i.InstanceID.toString() === selectedInstanceId.toString());
            const selectedDev = devices.find(d => d.DeviceID.toString() === (selectedInst?.DeviceID || '').toString());
            
            const devName  = selectedDev?.DeviceName || 'localhost';
            const ip       = selectedInterface || selectedInst?.IPAddress || 'localhost';
            const instName = selectedInst?.InstanceName || 'MSSQLSERVER';
            const port     = selectedInst?.Port || 1433;
            const login    = selectedLogin || 'sa';

            const connPrefix = `[${devName}|${ip}|${instName}|${port}|${login}]`;

            setLoadingDbs(true);
            setDbError(null);
            try {
                const res = await fetch(`/api/v3/databases?connPrefix=${encodeURIComponent(connPrefix)}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    if (data.error) {
                        setDbError(data.message || 'Failed to fetch databases');
                        setLiveDbs([]);
                        setSelectedDatabase('');
                    } else {
                        const dbList = Array.isArray(data) ? data : [];
                        setLiveDbs(dbList);
                        if (dbList.length > 0) {
                            setSelectedDatabase(dbList[0].DatabaseName);
                        } else {
                            setSelectedDatabase('');
                        }
                    }
                } else {
                    setLiveDbs([]);
                    setSelectedDatabase('');
                }
            } catch (err) {
                console.error('[ConnectionManager] Error loading databases:', err);
                setDbError(err.message);
                setLiveDbs([]);
                setSelectedDatabase('');
            } finally {
                setLoadingDbs(false);
            }
        };

        fetchLiveDatabases();
    }, [selectedInstanceId, selectedInterface, selectedLogin, instances, devices, token]);

    // Fetch available tables for the selected database
    useEffect(() => {
        const fetchTablesForDb = async () => {
            if (!selectedInstanceId || !selectedDatabase) {
                setTables([]);
                setSelectedTable('');
                return;
            }
            const selectedInst = instances.find(i => i.InstanceID.toString() === selectedInstanceId.toString());
            const selectedDev = devices.find(d => d.DeviceID.toString() === (selectedInst?.DeviceID || '').toString());
            if (!selectedInst) return;

            const devName  = selectedDev?.DeviceName || 'localhost';
            const ip       = selectedInterface || selectedInst?.IPAddress || 'localhost';
            const instName = selectedInst?.InstanceName || 'MSSQLSERVER';
            const port     = selectedInst?.Port || 1433;
            const login    = selectedLogin || 'sa';

            const connPrefix = `[${devName}|${ip}|${instName}|${port}|${login}]${selectedDatabase}`;

            setLoadingTables(true);
            try {
                const res = await fetch(`/api/v3/tables?targetPath=${encodeURIComponent(connPrefix)}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    const rawList = Array.isArray(data) ? data : (data.tables || []);
                    const tableList = rawList.map(t => {
                        if (typeof t === 'string') return t.includes('.') ? t : `dbo.${t}`;
                        return `${t.schemaName || t.SchemaName || 'dbo'}.${t.tableName || t.TableName || t}`;
                    });
                    setTables(tableList);
                    if (tableList.length > 0) {
                        setSelectedTable(tableList[0]);
                    } else {
                        setSelectedTable('');
                    }
                }
            } catch (err) {
                console.error('[ConnectionManager] Failed to fetch tables:', err);
            } finally {
                setLoadingTables(false);
            }
        };

        fetchTablesForDb();
    }, [selectedInstanceId, selectedDatabase, selectedInterface, selectedLogin, instances, devices, token]);

    const handleAdd = () => {
        const selectedInst = instances.find(i => i.InstanceID.toString() === selectedInstanceId.toString());
        const selectedDev = devices.find(d => d.DeviceID.toString() === (selectedInst?.DeviceID || '').toString());
        if (!selectedInst || !selectedTable || !selectedDatabase) return;
        
        const devName  = selectedDev?.DeviceName || 'localhost';
        const ip       = selectedInterface || selectedInst?.IPAddress || 'localhost';
        const instName = selectedInst?.InstanceName || 'MSSQLSERVER';
        const port     = selectedInst?.Port || 1433;
        const login    = selectedLogin || 'sa';
        
        const connectionString = `[${devName}|${ip}|${instName}|${port}|${login}]${selectedDatabase}`;
        onAddConnection({ connectionString, rootTable: selectedTable });
    };

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[200]">
            <div className="bg-white p-6 rounded-lg shadow-2xl border border-gray-200 w-[600px] text-gray-900">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Build Database Connection</h3>
                <p className="text-sm text-gray-600 mb-6">
                    Navigate the infrastructure tree to define your database connection and target root table.
                </p>

                {error && <div className="text-red-500 text-sm mb-4">{error}</div>}

                <div className="space-y-4 max-h-[60vh] overflow-y-auto p-1">
                    {loading ? (
                        <div className="text-sm text-gray-500">Loading infrastructure data...</div>
                    ) : (
                        <>
                            {/* 1. Organization */}
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">1. Organization</label>
                                <select 
                                    value={selectedOrgId} 
                                    onChange={e => setSelectedOrgId(e.target.value)}
                                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-black font-mono"
                                >
                                    {renderOrgOptions(organizations)}
                                </select>
                            </div>

                            {/* 2. Device */}
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">2. Device</label>
                                <select 
                                    value={selectedDeviceId} 
                                    onChange={e => setSelectedDeviceId(e.target.value)}
                                    disabled={availableDevices.length === 0}
                                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-black disabled:bg-gray-100"
                                >
                                    {availableDevices.length === 0 ? <option value="">No devices found in this Org</option> : null}
                                    {availableDevices.map(d => (
                                        <option key={d.DeviceID} value={d.DeviceID}>{d.DeviceName}</option>
                                    ))}
                                </select>
                            </div>

                            {/* 3. SQL Instance */}
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">3. SQL Instance</label>
                                <select 
                                    value={selectedInstanceId} 
                                    onChange={e => setSelectedInstanceId(e.target.value)}
                                    disabled={availableInstances.length === 0}
                                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-black disabled:bg-gray-100"
                                >
                                    {availableInstances.length === 0 ? <option value="">No SQL instances found on Device</option> : null}
                                    {availableInstances.map(inst => (
                                        <option key={inst.InstanceID} value={inst.InstanceID}>{inst.InstanceName || inst.ServerName} (Port: {inst.Port || 1433})</option>
                                    ))}
                                </select>
                            </div>

                            {/* 4. Network Interface */}
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">4. Network Interface</label>
                                <select 
                                    value={selectedInterface} 
                                    onChange={e => setSelectedInterface(e.target.value)}
                                    disabled={availableInterfaces.length === 0}
                                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-black disabled:bg-gray-100"
                                >
                                    {availableInterfaces.length === 0 ? <option value="">No interfaces found on Device</option> : null}
                                    {availableInterfaces.map(ifc => (
                                        <option key={ifc.InterfaceID || ifc.IPAddress} value={ifc.IPAddress}>
                                            {ifc.IPAddress} {ifc.IPType ? `(${ifc.IPType})` : ''} {!ifc.IsOnline ? '[OFFLINE]' : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* 5. Login / Credential */}
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">5. Vault Login</label>
                                <select 
                                    value={selectedLogin} 
                                    onChange={e => setSelectedLogin(e.target.value)}
                                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-black"
                                >
                                    {availableCredentials.length === 0 ? <option value="sa">sa (default)</option> : null}
                                    {availableCredentials.map(c => (
                                        <option key={c.CredentialID} value={c.Username}>{c.Username}</option>
                                    ))}
                                </select>
                            </div>

                            {/* 6. Database */}
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">6. Database</label>
                                <select 
                                    value={selectedDatabase} 
                                    onChange={e => setSelectedDatabase(e.target.value)}
                                    disabled={loadingDbs || liveDbs.length === 0}
                                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-black disabled:bg-gray-100 font-medium"
                                >
                                    {loadingDbs ? (
                                        <option value="">Querying live databases from instance...</option>
                                    ) : dbError ? (
                                        <option value="">Connection error: {dbError}</option>
                                    ) : liveDbs.length === 0 ? (
                                        <option value="">No databases accessible on this Instance</option>
                                    ) : (
                                        liveDbs.map(db => (
                                            <option key={db.DatabaseName} value={db.DatabaseName}>{db.DatabaseName}</option>
                                        ))
                                    )}
                                </select>
                            </div>

                            {/* 7. Target Root Table */}
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">7. Target Root Table</label>
                                <select 
                                    value={selectedTable} 
                                    onChange={e => setSelectedTable(e.target.value)}
                                    disabled={loadingTables || tables.length === 0}
                                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-black disabled:bg-gray-100 font-medium"
                                >
                                    {loadingTables ? (
                                        <option value="">Loading tables from database...</option>
                                    ) : tables.length === 0 ? (
                                        <option value="">No tables found in this database</option>
                                    ) : (
                                        tables.map(tbl => (
                                            <option key={tbl} value={tbl}>{tbl}</option>
                                        ))
                                    )}
                                </select>
                            </div>
                        </>
                    )}
                </div>

                <div className="flex justify-end gap-3 mt-8">
                    <button 
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                    >
                        Cancel
                    </button>
                    <button 
                        onClick={handleAdd}
                        disabled={loading || loadingDbs || loadingTables || !selectedInstanceId || !selectedDatabase || !selectedTable}
                        className="px-4 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50 transition-colors shadow-sm"
                    >
                        Connect Table
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConnectionManager;
